import {
  Bitcoin,
  Cpu,
  DollarSign,
  Earth,
  Gamepad2,
  Globe2,
  Landmark,
  Leaf,
  LineChart,
  MessageCircle,
  Palette,
  Rocket,
  Sparkles,
  TrendingUp,
  Trophy,
  Vote,
  Zap,
  Bookmark,
  Wheat,
} from "lucide-react";
import { TbBallFootball } from "react-icons/tb";

/**
 * Pure category configuration for the markets page.
 *
 * Lives outside MarketsPageContent so the closure stays smaller and these
 * tables can be reused (or unit-tested) without dragging the full page in.
 * No state, no hooks — purely lookup tables and 1-line predicates.
 *
 * NOTE: predictFallbackCategories stays inline in MarketsPageContent.tsx
 * because its "Live" entry uses a JSX-factory icon (a pulsing red dot).
 * Moving it here would need this file to be .tsx; the marginal gain isn't
 * worth the extra build surface.
 */

export const ELECTIONS_SUBCATEGORY_IDS = ["macro", "fed-rates", "trump"] as const;

export const isElectionsSubcategory = (cat: string): boolean =>
  (ELECTIONS_SUBCATEGORY_IDS as readonly string[]).includes(cat);

export const resolveCategory = (cat: string): string =>
  isElectionsSubcategory(cat) ? "elections" : cat;

// Polymarket category icons — used by both the dynamic taxonomy mapper and
// the fallback list below.
export const categoryIconMap: Record<string, any> = {
  politics: Landmark,
  sports: TbBallFootball,
  esports: Gamepad2,
  crypto: Bitcoin,
  finance: DollarSign,
  geopolitics: Globe2,
  tech: Cpu,
  culture: Palette,
  world: Earth,
  economy: LineChart,
  "climate-science": Leaf,
  mentions: MessageCircle,
  elections: Vote,
};

// Static prefix categories (FrenFlow-specific, always present)
export const staticPrefixCategories = [
  { id: "for-you", label: "For You", icon: Sparkles },
  { id: "bookmarks", label: "Bookmarks", icon: Bookmark },
  { id: "trending", label: "Trending", icon: TrendingUp },
  { id: "breaking", label: "Breaking", icon: Zap },
  { id: "new", label: "New", icon: Globe2 },
  { id: "elon", label: "Elon Musk", icon: Rocket },
];

// Fallback used while the gamma taxonomy fetch hasn't resolved.
export const fallbackDynamicCategories = [
  { id: "politics", label: "Politics", icon: Landmark },
  { id: "sports", label: "Sports", icon: TbBallFootball },
  { id: "esports", label: "Esports", icon: Gamepad2 },
  { id: "crypto", label: "Crypto", icon: Bitcoin },
  { id: "finance", label: "Finance", icon: DollarSign },
  { id: "geopolitics", label: "Geopolitics", icon: Globe2 },
  { id: "tech", label: "Tech", icon: Cpu },
  { id: "culture", label: "Culture", icon: Palette },
  { id: "world", label: "World", icon: Earth },
  { id: "economy", label: "Economy", icon: LineChart },
  { id: "weather", label: "Weather", icon: Leaf },
  { id: "mentions", label: "Mentions", icon: MessageCircle },
];

// Kalshi category icons — keyed by their UI labels (Kalshi's API returns
// label strings, not slugs).
export const kalshiIconMap: Record<string, any> = {
  Trending: TrendingUp,
  Politics: Landmark,
  Sports: Trophy,
  Entertainment: Palette,
  Culture: Palette,
  Crypto: Bitcoin,
  Climate: Leaf,
  Economics: LineChart,
  Mentions: MessageCircle,
  Companies: Cpu,
  Financials: DollarSign,
  Science: Globe2,
  Transportation: Globe2,
  "Tech & Science": Cpu,
  Commodities: Wheat,
};

// Predict.fun category icons.
export const predictIconMap: Record<string, any> = {
  Trending: TrendingUp,
  New: Zap,
  Sports: Trophy,
  Politics: Landmark,
  Crypto: Bitcoin,
  Esports: Gamepad2,
  Finance: DollarSign,
  Economy: LineChart,
  Culture: Palette,
  Tech: Cpu,
  Science: Globe2,
  Entertainment: Palette,
  "World & Politics": Landmark,
  "Finance & Economy": LineChart,
  "Entertainment & Culture": Palette,
};

// Hyperliquid HIP-4 categories — outcomeMeta has no taxonomy field, so we
// expose only the meta-rails (Trending, New) plus the actual question types
// we know HL ships today: Recurring (BTC/HYPE binary) and one-shot question
// markets (everything else). Real categories will plug in here once HL
// adds a taxonomy or once mainnet has enough markets to bucket them.
export const hyperliquidCategories = [
  { id: "trending", label: "Trending", icon: TrendingUp },
  { id: "new", label: "New", icon: Zap },
  { id: "recurring", label: "Recurring", icon: Bitcoin },
  { id: "crypto", label: "Crypto", icon: Bitcoin },
  { id: "events", label: "Events", icon: Globe2 },
];
